import React, {
  ChangeEvent,
  Dispatch,
  MouseEvent,
  SetStateAction,
  useState,
  useEffect,
} from "react";
import {
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Autocomplete,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { EventFormData, ITodo } from "./EventCalendar";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { Alert } from "@mui/material";
import BasicSelect from "../BasicSelect";
import axios from "axios";

/**
 * Gets the JWT token from cookies
 *
 * @returns {string} The JWT token
 */
const getAuthToken = () => {
  const cookies = document.cookie.split(";");
  let token = null;

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "_auth") {
      token = decodeURIComponent(value);
      break;
    }
  }

  return token;
};

interface AttendeeSuggestion {
  uid: number;
  name: string;
  email: string;
}

interface IProps {
  open: boolean;
  handleClose: Dispatch<SetStateAction<void>>;
  eventFormData: EventFormData;
  setEventFormData: Dispatch<SetStateAction<EventFormData>>;
  onAddEvent: (e: MouseEvent<HTMLButtonElement>) => void;
  todos: ITodo[];
  timeSlotAvailable: boolean; // indicating if time slot is available
  start: string;
  end: string;
  guestKey?: string; // Add guestKey prop for guest users
}

interface Company {
  tid: number | string;
  name: string;
  is_active: number | boolean;
  // Add other properties if present in the API response
}

const AddEventModal = ({
  open,
  handleClose,
  eventFormData,
  setEventFormData,
  onAddEvent,
  todos,
  timeSlotAvailable,
  start,
  end,
  guestKey,
}: IProps) => {
  const authUser = useAuthUser<any>();
  const { description } = eventFormData;
  const [companiesList, setCompaniesList] = useState<Company[]>([]);
  const requiresCompanySelection = authUser?.typeId >= 3;
  const hasSelectedCompany =
    Number.isInteger(eventFormData.companyId) && eventFormData.companyId > 0;
  const [loading, setLoading] = useState<boolean>(false); // State for loading
  const [attendeeSuggestions, setAttendeeSuggestions] = useState<
    AttendeeSuggestion[]
  >([]);
  const [attendeeInputValue, setAttendeeInputValue] = useState(""); // For manual email input
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // API endpoints
  const getCompaniesApi =
    process.env.REACT_APP_API_URL + "/api/get-companies-list";

  // Fetch rooms from the API
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        // Get the JWT token from cookies
        const token = getAuthToken();

        // Only proceed if token exists
        if (!token) {
          console.log("User not authenticated, skipping company fetch.");
          setCompaniesList([]); // Clear companies list if not logged in
          return;
        }

        // Include the token in the request headers
        const response = await axios.get<Company[]>(getCompaniesApi, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCompaniesList(response.data);
        //console.log('Companies API Response:', response.data);
      } catch (error) {
        console.error("Error fetching Companies:", error);
        // Handle error, show error message, etc.
      }
    };

    // Call fetchCompanies unconditionally, but it now checks for token inside
    fetchCompanies();
  }, [getCompaniesApi]);

  useEffect(() => {
    if (open) {
      const fetchAttendeeSuggestions = async () => {
        setLoadingSuggestions(true);
        try {
          const token = getAuthToken();
          if (!token) {
            console.error("Auth token not found for fetching suggestions.");
            setLoadingSuggestions(false);
            return;
          }
          const response = await axios.get(
            `${
              process.env.REACT_APP_API_URL || "http://localhost:5000"
            }/api/get-attendee-suggestions`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (response.data && Array.isArray(response.data)) {
            setAttendeeSuggestions(response.data);
          } else {
            setAttendeeSuggestions([]);
          }
        } catch (error) {
          console.error("Error fetching attendee suggestions:", error);
          setAttendeeSuggestions([]);
        } finally {
          setLoadingSuggestions(false);
        }
      };

      fetchAttendeeSuggestions();
    }
  }, [open]); // Re-fetch when modal opens

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEventFormData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleTodoChange = (e: React.SyntheticEvent, value: ITodo | null) => {
    setEventFormData((prevState) => ({
      ...prevState,
      todoId: value?._id,
    }));
  };

  const onCompanyChange = (value: number | string) => {
    const companyId = Number(value);
    setEventFormData((prevState) => ({
      ...prevState,
      companyId: Number.isInteger(companyId) && companyId > 0 ? companyId : 0,
    }));
  };

  const generateAlerts = () => {
    const alerts = [];

    if (requiresCompanySelection && !hasSelectedCompany) {
      alerts.push({
        variant: "outlined" as "outlined",
        severity: "error" as "error",
        message: "Please select a company before booking.",
      });
    }

    // Only show sign-in warning if user is not authenticated AND has no guest key
    if (authUser === null && !guestKey) {
      alerts.push({
        variant: "outlined" as "outlined",
        severity: "error" as "error",
        message:
          "You must sign in to an account to book. If you do not have an account, please call +918779657061 for assistance.",
      });
    }

    if (!timeSlotAvailable && authUser?.typeId < 3) {
      alerts.push({
        variant: "outlined" as "outlined",
        severity: "error" as "error",
        message: "Time slot unavailable.",
      });
    }

    // Add a special alert for authUser with typeId 4 and timeSlot not available
    if (authUser !== null && authUser?.typeId >= 3 && !timeSlotAvailable) {
      alerts.push({
        variant: "outlined" as "outlined",
        severity: "warning" as "warning", // You can change the severity accordingly
        message: "Time selected is in the past or overlaps another event.",
      });
    }

    return alerts.map((alert, index) => (
      <Alert
        key={index}
        variant={alert.variant}
        severity={alert.severity}
        sx={{ marginBottom: 1 }}
      >
        {alert.message}
      </Alert>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>
        {description ? description : "New Meeting"} ({start} - {end})
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          To book a meeting room, please fill in the information below.
        </Box>
        <Box component='form'>
          {/* Email field for guest users */}
          {!authUser && guestKey && (
            <>
              <TextField
                sx={{ mt: 3 }}
                name='email'
                value={eventFormData.email || ""}
                margin='dense'
                id='email'
                label='Your Email Address'
                type='email'
                fullWidth
                variant='outlined'
                onChange={onChange}
                required
                helperText='Required for guest bookings'
              />
              <TextField
                sx={{ mt: 3 }}
                name='companyName'
                value={eventFormData.companyName || ""}
                margin='dense'
                id='companyName'
                label='Company/Organization Name'
                type='text'
                fullWidth
                variant='outlined'
                onChange={onChange}
                helperText='Optional - helps identify your booking'
              />
            </>
          )}

          {/* Company selector for authenticated admin users */}
          {requiresCompanySelection && (
            <Box sx={{ mt: 3 }}>
              <BasicSelect
                label='Company'
                itemList={companiesList
                  .filter(
                    (company) => company.is_active === 1 || company.is_active === true
                  )
                  .map((company) => ({
                    value: Number(company.tid),
                    label: company.name,
                  }))}
                selectedValue={hasSelectedCompany ? eventFormData.companyId : ""}
                onValueChange={onCompanyChange}
              />
            </Box>
          )}

          <TextField
            sx={{ mt: 3 }}
            name='description'
            value={description}
            margin='dense'
            id='description'
            label='Description'
            type='text'
            fullWidth
            variant='outlined'
            onChange={onChange}
          />
          {/* Hidden for now */}
          <Autocomplete
            onChange={handleTodoChange}
            disablePortal
            id='combo-box-demo'
            options={todos}
            sx={{ marginTop: 3, display: "none" }}
            getOptionLabel={(option) => option.title}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Todo'
              />
            )}
          />
          <TextField
            sx={{ mt: 3 }}
            name='start'
            value={start}
            margin='dense'
            id='start'
            label='Start Time'
            type='datetime-local'
            fullWidth
            variant='outlined'
            onChange={onChange}
            helperText={!timeSlotAvailable ? "Time slot is not available" : ""}
            FormHelperTextProps={{ sx: { textAlign: "center" } }}
            InputProps={{
              readOnly: true, // Make this field read-only
            }}
            style={{ display: "none" }} // Hide the field
          >
            {`${start} - ${end}`}
          </TextField>
        </Box>

        {/* Display Start and End Times */}
        {/* 
        <TextField
          sx={{ mt: 2, textAlign: "center" }}
          label='Selected Time Slot'
          value={`${start} - ${end}`}
          InputProps={{
            readOnly: true,
          }}
          fullWidth
          variant='filled'
          error={!timeSlotAvailable}
          helperText={!timeSlotAvailable ? "Time slot is not available" : ""}
          FormHelperTextProps={{ sx: { textAlign: "center" } }}
        />
        */}

        {/* Attendee Management Section - Hide for guest users */}
        {authUser && (
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography
              variant='subtitle1'
              gutterBottom
              sx={{ fontWeight: "medium" }}
            >
              Attendees
            </Typography>

            {/* Autocomplete for suggestions */}
            <Autocomplete
              multiple
              id='attendee-suggestions-autocomplete'
              options={attendeeSuggestions}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={attendeeSuggestions.filter((suggestion) =>
                eventFormData.attendees.includes(suggestion.email)
              )}
              onChange={(event, newValue) => {
                setEventFormData((prev) => ({
                  ...prev,
                  attendees: newValue.map((item) => item.email),
                }));
              }}
              loading={loadingSuggestions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='outlined'
                  label='Select or Search Attendees from your Organization'
                  placeholder='Add users...'
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSuggestions ? (
                          <CircularProgress
                            color='inherit'
                            size={20}
                          />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  key={option.uid}
                >
                  {option.name} ({option.email})
                </li>
              )}
              isOptionEqualToValue={(option, value) =>
                option.email === value.email
              }
              sx={{ mb: 2 }}
              noOptionsText={
                loadingSuggestions ? "Loading..." : "No suggestions found"
              }
            />

            {/* Manual email input */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                label='Add External Attendee by Email'
                variant='outlined'
                value={attendeeInputValue}
                onChange={(e) => setAttendeeInputValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && attendeeInputValue.trim()) {
                    e.preventDefault();
                    const newEmail = attendeeInputValue.trim();
                    if (
                      newEmail &&
                      !eventFormData.attendees.includes(newEmail)
                    ) {
                      setEventFormData((prev) => ({
                        ...prev,
                        attendees: [...prev.attendees, newEmail],
                      }));
                    }
                    setAttendeeInputValue("");
                  }
                }}
                fullWidth
                sx={{ mr: 1 }}
                type='email'
              />
              <Button
                variant='outlined'
                onClick={() => {
                  const newEmail = attendeeInputValue.trim();
                  if (newEmail && !eventFormData.attendees.includes(newEmail)) {
                    setEventFormData((prev) => ({
                      ...prev,
                      attendees: [...prev.attendees, newEmail],
                    }));
                  }
                  setAttendeeInputValue("");
                }}
                disabled={
                  !attendeeInputValue.trim() ||
                  !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(attendeeInputValue)
                }
              >
                Add
              </Button>
            </Box>

            {/* Display selected attendees as Chips */}
            <Typography
              variant='caption'
              display='block'
              gutterBottom
            >
              Current Attendees:
            </Typography>
            {eventFormData.attendees.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  p: 0.5,
                  border: "1px dashed grey",
                  borderRadius: 1,
                  minHeight: "40px",
                }}
              >
                {eventFormData.attendees.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => {
                      setEventFormData((prev) => ({
                        ...prev,
                        attendees: prev.attendees.filter(
                          (item) => item !== email
                        ),
                      }));
                    }}
                    size='small'
                  />
                ))}
              </Box>
            ) : (
              <Typography
                variant='body2'
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                No attendees added yet. The event creator will be added
                automatically.
              </Typography>
            )}
          </Box>
        )}
        {/* End of Attendee Management Section */}
      </DialogContent>
      <DialogActions>
        <Grid
          container
          spacing={2}
          direction='column'
          alignItems={"center"}
        >
          <Grid item>
            <Button
              onClick={handleClose}
              variant='outlined'
              sx={{ marginRight: 1 }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                loading ||
                (requiresCompanySelection && !hasSelectedCompany) ||
                (!timeSlotAvailable &&
                  (authUser === null || authUser.typeId < 3)) ||
                description === "" ||
                // For guest users, require email
                (!authUser && guestKey && !eventFormData.email)
              }
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                setLoading(true); // Set loading state to true
                onAddEvent(e); // Pass the MouseEvent parameter to onAddEvent function
                // Set loading back to false after 5 seconds
                setTimeout(() => {
                  setLoading(false);
                }, 5000);
              }}
              variant='contained'
            >
              {loading ? "Booking..." : "Book"}
            </Button>
          </Grid>
          <Grid item>{generateAlerts()}</Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default AddEventModal;
